import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { LicenseOption } from "../../../api/license";
import { I18nKey } from "../../../i18n/i18n";
import { FilterableValue } from "../../common/FilterableValue";

/**
 * Libelle et icone d'un code licence.
 *
 * Ces deux tables vivaient dans `LicenseModal.tsx`, non exportees. L'historique des
 * licences en a besoin aussi : plutot que de les recopier (et de les voir diverger au
 * premier code ajoute), elles sont ici et le modal les importe.
 */
export const licenseToI18nKey: Record<LicenseOption, I18nKey> = {
  // i18n.t("license-alimentation-precision")
  alimentation_precision: "license-alimentation-precision",
  // i18n.t("license-compteur-elec")
  compteur_elec: "license-compteur-elec",
  // i18n.t("license-compteur-gaz")
  compteur_gaz: "license-compteur-gaz",
  // i18n.t("license-data-extraction")
  data_extraction: "license-data-extraction",
  // i18n.t("license-hygro-ext")
  hygro_ext: "license-hygro-ext",
  // i18n.t("license-maj-usb")
  maj_usb: "license-maj-usb",
  // i18n.t("license-pesage-silo")
  pesage_silo: "license-pesage-silo",
  // i18n.t("license-pesage-volaille")
  pesage_volaille: "license-pesage-volaille",
  // i18n.t("license-security-codes")
  security_codes: "license-security-codes",
  // i18n.t("license-ventilation-jardin")
  ventilation_jardin: "license-ventilation-jardin",
};

export const licenseToIcon: Record<LicenseOption, IconProp> = {
  alimentation_precision: "screwdriver-wrench",
  compteur_elec: "bolt",
  compteur_gaz: "fire-flame-simple",
  data_extraction: "download",
  hygro_ext: "droplet",
  maj_usb: "sync",
  pesage_silo: "building",
  pesage_volaille: "crown",
  security_codes: "lock",
  ventilation_jardin: "fan",
};

/** Vrai si `code` est un code licence connu du front (garde pour les donnees historiques). */
export const isKnownLicense = (code: string): code is LicenseOption =>
  code in licenseToIcon;

/**
 * Une licence : icone + libelle traduit.
 *
 * L'historique stocke les codes BRUTS (`pesage_silo`) : un code retire du catalogue reste
 * donc en base pour toujours. On n'affiche l'icone que pour les codes connus et on retombe
 * sur le code brut sinon — une ligne d'audit doit rester lisible meme apres evolution du
 * catalogue, jamais disparaitre ni casser le rendu.
 */
export const LicenseBadge = ({ code }: { code: string }) => {
  const { t } = useTranslation();

  if (!isKnownLicense(code)) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-oxaniumMedium bg-grey-light text-grey-dark">
        {code}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-oxaniumMedium bg-grey-light text-grey-dark">
      <FontAwesomeIcon icon={licenseToIcon[code]} />
      <span>{t(licenseToI18nKey[code])}</span>
    </span>
  );
};

/**
 * Liste de codes telle que stockee dans l'historique : « a,b,c » ou « a, b, c ».
 * Le back n'impose pas d'espace apres la virgule, d'ou le `trim`.
 */
export const LicenseCodeList = ({
  codes,
  onCodeClick,
  activeCode,
}: {
  codes?: string;
  /**
   * Rend chaque code cliquable (historique : « montrer les lignes de ce code »).
   * Omis, la liste reste inerte — un badge de licence n'a pas vocation à filtrer partout.
   */
  onCodeClick?: (code: string) => void;
  /** Code déjà filtré : affiché mais non cliquable, pour ne pas empiler un doublon. */
  activeCode?: string;
}) => {
  const { t } = useTranslation();
  const list = (codes ?? "")
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c !== "");

  if (list.length === 0) return <>-</>;

  return (
    <span className="inline-flex flex-wrap gap-1">
      {list.map((code) =>
        onCodeClick ? (
          <FilterableValue
            key={code}
            variant="badge"
            label={<LicenseBadge code={code} />}
            active={activeCode === code}
            onFilter={() => onCodeClick(code)}
            title={t("filter-title-license")}
          />
        ) : (
          <LicenseBadge key={code} code={code} />
        )
      )}
    </span>
  );
};
